/**
 * Subscription Tiers Configuration
 * Defines features and pricing for each tier
 */

import type { TierConfig, SubscriptionTier, FeatureKey } from '@/types/subscription';

export const SUBSCRIPTION_TIERS: Record<SubscriptionTier, TierConfig> = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Basic air quality monitoring',
    priceUSD: 0,
    features: {
      basicAqiView: true,
      apiCallsPerMonth: 100,
      mapAccess: 'basic',
      aiChat: false,
      routePlanning: false,
      alerts: false,
      forecast: '1hr',
      export: false,
      teamManagement: false,
      apiAccess: false,
      prioritySupport: false,
      whiteLabel: false,
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'Full features for health-conscious individuals',
    priceUSD: 8.99,
    priceAnnualUSD: 89.99,
    highlighted: true,
    badge: 'Most Popular',
    features: {
      basicAqiView: true,
      apiCallsPerMonth: 10000,
      mapAccess: 'full',
      aiChat: true,
      routePlanning: true,
      alerts: true,
      forecast: '24hr',
      export: true,
      teamManagement: false,
      apiAccess: false,
      prioritySupport: false,
      whiteLabel: false,
    },
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Complete solution for teams and businesses',
    priceUSD: 19.99,
    priceAnnualUSD: 199.99,
    badge: 'Best Value',
    features: {
      basicAqiView: true,
      apiCallsPerMonth: 100000,
      mapAccess: 'full',
      aiChat: true,
      routePlanning: true,
      alerts: true,
      forecast: '48hr',
      export: true,
      teamManagement: true,
      apiAccess: true,
      prioritySupport: true,
      whiteLabel: true,
    },
  },
};

export const FEATURE_NAMES: Record<FeatureKey, string> = {
  basicAqiView: 'Air Quality Monitoring',
  mapAccess: 'Map Layers & Heatmaps',
  aiChat: 'AI Assistant',
  routePlanning: 'Smart Route Planning',
  alerts: 'Custom Air Quality Alerts',
  forecast: 'Extended Forecasts',
  export: 'Data Export',
  teamManagement: 'Team Management',
  apiAccess: 'API Access',
  prioritySupport: 'Priority Support',
  whiteLabel: 'White Label Options',
};

export const FEATURE_DESCRIPTIONS: Record<FeatureKey, string> = {
  basicAqiView: 'Real-time air quality index for your location',
  mapAccess: 'Pollution heatmaps, plume visualization, and all map layers',
  aiChat: 'AI-powered assistant for health recommendations',
  routePlanning: 'Optimize routes for best air quality',
  alerts: 'Get notified when air quality changes',
  forecast: 'Plan ahead with extended air quality forecasts',
  export: 'Download your air quality data',
  teamManagement: 'Manage team members and permissions',
  apiAccess: 'Integrate with your own applications',
  prioritySupport: 'Fast response times and dedicated support',
  whiteLabel: 'Custom branding for your organization',
};

/**
 * Get the minimum tier required for a feature
 */
export function getRequiredTier(feature: FeatureKey): SubscriptionTier {
  if (SUBSCRIPTION_TIERS.free.features[feature]) return 'free';
  if (SUBSCRIPTION_TIERS.pro.features[feature]) return 'pro';
  return 'enterprise';
}

/**
 * Check if a tier has access to a feature
 */
export function tierHasFeature(tier: SubscriptionTier, feature: FeatureKey): boolean {
  const config = SUBSCRIPTION_TIERS[tier];
  const featureValue = config.features[feature];

  // Handle boolean features
  if (typeof featureValue === 'boolean') {
    return featureValue;
  }

  // For non-boolean features (mapAccess, forecast), check if it's not the minimum
  return true;
}

/**
 * Get tier comparison for upgrade prompts
 */
export function getTierComparison(
  currentTier: SubscriptionTier,
  targetTier: SubscriptionTier
): { feature: string; current: string | boolean; target: string | boolean }[] {
  const current = SUBSCRIPTION_TIERS[currentTier].features;
  const target = SUBSCRIPTION_TIERS[targetTier].features;

  return (Object.keys(target) as FeatureKey[])
    .filter((key) => current[key] !== target[key])
    .map((key) => ({
      feature: FEATURE_NAMES[key],
      current: current[key],
      target: target[key],
    }));
}
